import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './AuthModal.css';
export const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
    const [mode, setMode] = useState(initialMode);
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [inviteOnlyMode, setInviteOnlyMode] = useState(false);
    const [isLoadingInviteMode, setIsLoadingInviteMode] = useState(true);
    const { login, register } = useAuth();
    // Fetch invite-only mode status
    useEffect(() => {
        const fetchInviteMode = async () => {
            try {
                const response = await fetch('/api/invites/mode');
                if (response.ok) {
                    const data = await response.json();
                    setInviteOnlyMode(data.inviteOnlyMode || false);
                }
            }
            catch (error) {
                console.error('Failed to fetch invite-only mode:', error);
            }
            finally {
                setIsLoadingInviteMode(false);
            }
        };
        fetchInviteMode();
    }, []);
    // Extract invite code from URL on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const inviteFromUrl = params.get('invite');
        if (inviteFromUrl) {
            setInviteCode(inviteFromUrl.toUpperCase());
            // Switch to register mode if invite code is present
            setMode('register');
        }
    }, []);
    // Update mode when initialMode prop changes
    useEffect(() => {
        setMode(initialMode);
    }, [initialMode]);
    if (!isOpen)
        return null;
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            if (mode === 'login') {
                await login(username, password);
            }
            else {
                if (!email || !username || !password || !phoneNumber) {
                    throw new Error('All fields are required');
                }
                if (password.length < 8) {
                    throw new Error('Password must be at least 8 characters');
                }
                // Validate phone number format client-side
                const normalizedPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
                if (!/^\+[1-9]\d{1,14}$/.test(normalizedPhone)) {
                    throw new Error('Phone number must be in international format (e.g., +12025551234)');
                }
                // Validate invite code when in invite-only mode
                if (inviteOnlyMode && !inviteCode) {
                    throw new Error('Invite code is required when the site is in invite-only mode');
                }
                await register(email, username, password, phoneNumber, inviteCode || undefined);
            }
            // Success - close modal
            onClose();
        }
        catch (err) {
            setError(err.message || 'Authentication failed');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const switchMode = () => {
        setMode(mode === 'login' ? 'register' : 'login');
        setError('');
    };
    return (_jsx("div", { className: "auth-modal-overlay", onClick: onClose, children: _jsxs("div", { className: "auth-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("button", { className: "auth-modal-close", onClick: onClose, children: "\u00C3\u2014" }), _jsx("h2", { children: mode === 'login' ? 'Welcome' : 'Create Account' }), mode === 'register' && inviteOnlyMode && !isLoadingInviteMode && (_jsxs("div", { style: {
                        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                        border: '1px solid rgba(168, 85, 247, 0.3)',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '16px',
                        marginTop: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }, children: [_jsx("span", { style: { fontSize: '20px' }, children: "\u00F0\u0178\u201D\u2019" }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("div", { style: { fontWeight: 600, color: 'white', marginBottom: '2px' }, children: "Invite-Only Registration" }), _jsx("div", { style: { fontSize: '12px', color: 'rgba(255, 255, 255, 0.85)' }, children: "This site currently requires an invite code to register. Please enter your code below." })] })] })), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "username", children: "Username" }), _jsx("input", { type: "text", id: "username", value: username, onChange: (e) => setUsername(e.target.value), placeholder: mode === 'login' ? 'Username or email' : 'Choose a username', required: true, disabled: isSubmitting })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "password", children: "Password" }), _jsx("input", { type: "password", id: "password", value: password, onChange: (e) => setPassword(e.target.value), placeholder: mode === 'register' ? 'At least 8 characters' : 'Your password', required: true, disabled: isSubmitting })] }), mode === 'register' && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "email", children: "Email" }), _jsx("input", { type: "email", id: "email", value: email, onChange: (e) => setEmail(e.target.value), placeholder: "your@email.com", required: true, disabled: isSubmitting })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "phoneNumber", children: "Phone Number" }), _jsx("input", { type: "tel", id: "phoneNumber", value: phoneNumber, onChange: (e) => setPhoneNumber(e.target.value), placeholder: "+12025551234", required: true, disabled: isSubmitting }), _jsx("small", { style: { color: '#888', fontSize: '11px', marginTop: '4px', display: 'block' }, children: "Include country code (e.g., +1 for US, +44 for UK)" })] }), inviteOnlyMode && !isLoadingInviteMode && (_jsxs("div", { className: "form-group", children: [_jsxs("label", { htmlFor: "inviteCode", children: ["Invite Code ", inviteOnlyMode && _jsx("span", { style: { color: '#ef4444' }, children: "*" })] }), _jsx("input", { type: "text", id: "inviteCode", value: inviteCode, onChange: (e) => setInviteCode(e.target.value.toUpperCase()), placeholder: "Enter your 8-character code", disabled: isSubmitting, maxLength: 8, style: { textTransform: 'uppercase' }, required: inviteOnlyMode })] }))] })), error && _jsx("div", { className: "auth-error", children: error }), _jsx("button", { type: "submit", className: "auth-submit-btn", disabled: isSubmitting, children: isSubmitting
                                ? 'Please wait...'
                                : mode === 'login' ? 'Sign In' : 'Create Account' })] }), _jsx("div", { className: "auth-switch", children: mode === 'login' ? (_jsxs(_Fragment, { children: ["Don't have an account?", ' ', _jsx("button", { onClick: switchMode, className: "auth-switch-btn", children: "Sign up" })] })) : (_jsxs(_Fragment, { children: ["Already have an account?", ' ', _jsx("button", { onClick: switchMode, className: "auth-switch-btn", children: "Sign in" })] })) })] }) }));
};
